import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  supportAgentApi,
  ChatDoc,
  ChatReviewInput,
  CHAT_REVIEW_ARTICLE_REASONS,
  CHAT_REVIEW_NOTE_MAX_CHARS,
  CHAT_REVIEW_REASONS,
} from './support-agent-api';
import { escalationReasonLabel, reviewKindFor } from './chat-queue';

const OTHER_ARTICLE = 'other';
const SAVE_ERROR_COPY = "Couldn't save the label — try again";

/** An article id from what the rep pasted: the id itself, or a KB link (`?kbDoc=<id>`). */
export function articleIdFrom(input: string): string | undefined {
  const match = input.match(/kbDoc=([0-9a-f]{24})\b/i) ?? input.trim().match(/^([0-9a-f]{24})$/i);
  return match ? match[1].toLowerCase() : undefined;
}

/** The articles an assistant reply in this chat cited, then the others it was given. */
function chatArticles(chat: ChatDoc): { id: string; cited: boolean }[] {
  const cited = new Set<string>();
  const given = new Set<string>();
  chat.turns
    .filter((turn) => turn.role === 'agent')
    .forEach((turn) => {
      (turn.citedDocIds ?? []).forEach((id) => cited.add(id));
      (turn.suppliedDocIds ?? []).forEach((id) => given.add(id));
    });
  return [
    ...Array.from(cited).map((id) => ({ id, cited: true })),
    ...Array.from(given)
      .filter((id) => !cited.has(id))
      .map((id) => ({ id, cited: false })),
  ];
}

/**
 * Support's label on a closed chat (the split view design, Michael 10/22-23;
 * pom-2-api #604), as a bar right under the chat where the reply box sits on
 * an open one (Michael, 10/23: "easier to label" — it used to be at the end
 * of the Details pane). An escalated chat asks why it needed a person, one
 * the customer rated "Not at all" without a person asks what went wrong:
 * one tap per reason. Two of the reasons also ask which article has the
 * right answer. These are the eval pipeline's labels; the customer never
 * sees them. Saving again replaces the label. Keyed by chat in the parent,
 * so a chat switch starts a fresh form from that chat's saved label.
 */
export function ChatLabelPanel({
  chat,
  titles,
  myEmail,
  inNeedsLabeling,
  onSaved,
  onFailed,
}: {
  chat: ChatDoc;
  titles: Record<string, string>;
  myEmail?: string;
  inNeedsLabeling: boolean;
  onSaved: (chat: ChatDoc) => void;
  // A save that failed: the parent says so if this chat is no longer open.
  onFailed?: (chat: ChatDoc) => void;
}) {
  const kind = reviewKindFor(chat);
  const saved = chat.review;
  const articles = chatArticles(chat);
  const savedArticleListed = saved?.articleId ? articles.some((a) => a.id === saved.articleId) : false;
  const [reason, setReason] = useState<string | null>(saved?.reason ?? null);
  const [article, setArticle] = useState<string | null>(
    saved?.articleId ? (savedArticleListed ? saved.articleId : OTHER_ARTICLE) : null,
  );
  const [otherArticle, setOtherArticle] = useState(saved?.articleId && !savedArticleListed ? saved.articleId : '');
  const [articleWrong, setArticleWrong] = useState(saved?.articleWrong === true);
  const [note, setNote] = useState(saved?.note ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  if (!kind) return null;
  const reasons: readonly { key: string; text: string; hint: string }[] = CHAT_REVIEW_REASONS[kind];
  const asksArticle = !!reason && CHAT_REVIEW_ARTICLE_REASONS.includes(reason);
  const articleId = article === OTHER_ARTICLE ? articleIdFrom(otherArticle) : article ?? undefined;
  const headingId = `chat-label-heading-${chat._id}`;
  const who = (email: string) => (myEmail && email === myEmail ? 'you' : email);

  const edited = () => {
    setStatus(null);
    setError(null);
  };

  const save = async () => {
    if (!reason || saving) return;
    if (asksArticle && article === OTHER_ARTICLE && !articleId) {
      setError("Paste the article's link or ID, or pick one above.");
      return;
    }
    const input: ChatReviewInput = {
      reason,
      ...(asksArticle && articleId ? { articleId } : {}),
      ...(asksArticle && articleId && articleWrong ? { articleWrong: true } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
    };
    setSaving(true);
    setError(null);
    // A failed re-save mustn't still read "Saved by you" beside its error.
    setStatus(null);
    try {
      const updated = await supportAgentApi.reviewChat(chat._id, input);
      onSaved(updated);
      setStatus(`Saved by you${inNeedsLabeling ? ' · off the Needs label list' : ''}`);
    } catch (err: any) {
      setError(err?.response?.data?.message || SAVE_ERROR_COPY);
      onFailed?.(chat);
    } finally {
      setSaving(false);
    }
  };

  const picked = reasons.find((r) => r.key === reason);
  return (
    <Box
      component="section"
      aria-labelledby={headingId}
      data-testid="chat-label-panel"
      sx={{ mt: 1, pt: 1.25, borderTop: 1, borderColor: 'divider' }}
    >
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap', rowGap: 0.25, mb: 1 }}>
        <Typography id={headingId} variant="subtitle2" component="h3" sx={{ fontWeight: 700 }}>
          {kind === 'rated' ? 'What went wrong?' : 'Why did it need a person?'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {kind === 'rated'
            ? 'The customer answered “Not at all”. The assistant handled it alone.'
            : `Escalated by: ${escalationReasonLabel(chat.escalation?.reason).toLowerCase()}`}
        </Typography>
        {saved && !status && (
          <Typography variant="caption" sx={{ color: 'success.darker', fontWeight: 600 }} data-testid="chat-label-saved">
            Labelled by {who(saved.by)} · {new Date(saved.at).toLocaleString()}
          </Typography>
        )}
        {/* In the heading line, so the bar stays short enough to keep Save in view. */}
        <Typography role="status" variant="caption" sx={{ color: 'success.darker', fontWeight: 600 }}>
          {status}
        </Typography>
      </Stack>
      {/* One tap per reason, right under the chat (Michael, 10/23: "easier to label"),
          on an even grid (Michael, 10/23: "organize the label buttons"): equal
          widths, as many across as the pane fits. */}
      <Box
        role="group"
        aria-labelledby={headingId}
        sx={{
          display: 'grid',
          // As many equal columns as fit with each label on one line.
          gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
          gap: 0.75,
        }}
      >
        {reasons.map((r) => {
          const on = reason === r.key;
          return (
            <ButtonBase
              key={r.key}
              aria-pressed={on}
              title={r.hint}
              onClick={() => {
                setReason(r.key);
                edited();
              }}
              sx={{
                px: 1.5,
                py: 0.5,
                minHeight: 34,
                textAlign: 'center',
                border: 1,
                borderColor: on ? 'primary.dark' : 'grey.400',
                borderRadius: 999,
                bgcolor: on ? 'primary.dark' : 'background.paper',
                color: on ? 'common.white' : 'grey.800',
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: 600,
                '&:hover': { borderColor: 'primary.dark' },
                '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.dark', outlineOffset: 2 },
              }}
            >
              {r.text}
            </ButtonBase>
          );
        })}
      </Box>
      {picked && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {picked.hint}
        </Typography>
      )}
      {asksArticle && (
        <Box component="fieldset" sx={{ border: 0, p: 0, m: 0, mt: 1.25 }}>
          <Typography component="legend" variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
            Which article has the right answer?
          </Typography>
          <RadioGroup
            value={article ?? ''}
            onChange={(e) => {
              setArticle(e.target.value);
              edited();
            }}
            sx={{ flexDirection: 'row', columnGap: 1.5 }}
          >
            {articles.map((a) => (
              <FormControlLabel
                key={a.id}
                value={a.id}
                control={<Radio size="small" />}
                label={
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    {titles[a.id] ?? `Article ${a.id.slice(-6)}`}
                    {a.cited ? ' (cited)' : ''}
                  </Typography>
                }
              />
            ))}
            <FormControlLabel
              value={OTHER_ARTICLE}
              control={<Radio size="small" />}
              label={<Typography variant="body2">Another article…</Typography>}
            />
          </RadioGroup>
          {article === OTHER_ARTICLE && (
            <TextField
              size="small"
              fullWidth
              label="Article link or ID"
              value={otherArticle}
              onChange={(e) => {
                setOtherArticle(e.target.value);
                edited();
              }}
              sx={{ mt: 0.5 }}
            />
          )}
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={articleWrong}
                disabled={!articleId}
                onChange={(e) => {
                  setArticleWrong(e.target.checked);
                  edited();
                }}
              />
            }
            label={<Typography variant="body2">That article is wrong or out of date</Typography>}
          />
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1 }}>
        <TextField
          size="small"
          fullWidth
          multiline
          maxRows={3}
          label="Note (optional)"
          placeholder="What should change?"
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            edited();
          }}
          inputProps={{ maxLength: CHAT_REVIEW_NOTE_MAX_CHARS }}
        />
        <Button
          variant="contained"
          disabled={!reason || saving}
          onClick={save}
          sx={{ height: 40, px: 2.5, flexShrink: 0, textTransform: 'none', fontWeight: 600, bgcolor: 'primary.dark' }}
        >
          {saving ? 'Saving…' : saved ? 'Update label' : 'Save label'}
        </Button>
      </Stack>
    </Box>
  );
}
